import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import cs from 'classnames';
import { format } from 'date-fns';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { WAGE_FORMAT_TITLE } from 'common/enums';
import { hasPermissionsFor } from 'helpers/_helpers';
import formatNumber from '../../utils/formatNumber';
import { getPayrollLastAction } from '../DayReport/payroll-state.helper';
import { getHoursToDate } from '../DayReport/components/CIPProjects/components/CIPProjectItem/CIPProjectItem';
import JobPrint from './JobPrint';
import { Field, Hours, Total } from './Rows';
import styles from './styles';

const useStyles = makeStyles(styles);

const Clock = ({ data }) => {
  const { formatForTimePars } = useTimeSettingsFormat()();

  return (
    <Total hours={formatNumber(data.modified?.hoursThisDay || data.hoursThisDay)}>
      <Field label="Clock-In:">
        {format(new Date(data.modified?.inTime || data.inTime), formatForTimePars)}
      </Field>
      <Field label="Clock-Out:">
        {data.outTime
          ? format(new Date(data.modified?.outTime || data.outTime), formatForTimePars)
          : '-'}
      </Field>
      <Field label="Lunch time:">{formatNumber(data.modified?.lunchTime || 0)}</Field>
    </Total>
  );
};

const Timeoff = ({ data }) => (
  <Total hours={formatNumber(data.thisDayDuration)}>
    <Field label="Time Off:">{data.reason}</Field>
    <Field>{data.isPTO ? 'Paid (PTO)' : 'Unpaid'}</Field>
    <Field>{data.type === 'partial' ? 'Partial day' : 'Full day'}</Field>
  </Total>
);

const Holiday = ({ data }) => (
  <Total hours={Number((data.hours * 60 + data.minutes) / 60).toFixed(2)}>
    <Field label="Holiday:">{data.name}</Field>
    <Field>Paid</Field>
  </Total>
);

const CipProject = ({ data: { cipProject, hoursTotal }, userId }) => (
  <Total hours={formatNumber(hoursTotal)}>
    <Field emph>{cipProject ? cipProject.name : 'N/A'}</Field>
    <Field label="Hours to date:">
      {cipProject ? formatNumber(getHoursToDate(cipProject.hoursPerUser, userId)) : 'N/A'}
    </Field>
  </Total>
);

const absence = (job) => {
  if (job.jobType === 'holiday') return <Holiday data={job} />;
  const timeoff = Array.isArray(job) ? job[0] : job;
  if (timeoff?.jobType === 'timeoff' || timeoff?.isTimeoff) return <Timeoff data={timeoff} />;
  return null;
};

const DayPrint = ({ title, summary }) => {
  const classes = useStyles();
  const { dateFormatWithTime } = useDateSettingsFormat();
  const payroll = summary?.payroll;

  const payrollNote = () => {
    if (!payroll || !hasPermissionsFor('payrollActions')) return null;
    const { by, at } = getPayrollLastAction(payroll, dateFormatWithTime);
    return payroll.verified ? `Verified by ${by}, time: ${at}` : `Edited by: ${by}, time: ${at}`;
  };

  const sectionItems = () => {
    const { user, timeclock, jobs, cipJobs } = summary;
    const absences = jobs.map(absence);
    const jobNodes = jobs.filter((_, i) => !absences[i]).map((job) => <JobPrint data={job} />);
    // shop hours must stay on paper on days with no worklogs
    if (!jobNodes.length && summary.shopTime > 0) jobNodes.push(null);

    return [
      ['Timeclock', timeclock.map((clock) => <Clock data={clock} />)],
      [
        <Total label="Shop time:" hours={formatNumber(summary.shopTime)}>
          Jobs
        </Total>,
        jobNodes,
      ],
      ['Time Off', absences.filter(Boolean)],
      ['CIP Projects', cipJobs.map((cip) => <CipProject data={cip} userId={user._id} />)],
    ].flatMap(([title, nodes], section) =>
      nodes.map((node, i) => ({
        key: `${section}-${i}`,
        title: !i && title,
        last: i === nodes.length - 1,
        node,
      }))
    );
  };

  const items = summary ? sectionItems() : [];
  // a day without section items still needs one block for the bar and its footer
  const blocks = items.length ? items : [{ key: 'day' }];

  const head = (
    <>
      <div className={classes.bar}>
        <span className={classes.barTitle}>{title}</span>
        <span className={classes.note}>{payrollNote()}</span>
      </div>
      {summary && (
        <div className={classes.user}>
          <Field label="Username:" emph>
            {summary.user.username}
          </Field>
          <Field label="HI code #:" emph>
            {summary.hiCode || summary.user.profile.HICode}
          </Field>
          {summary.user.profile?.wageFormat && (
            <Field label="Wage Format:">{WAGE_FORMAT_TITLE[summary.user.profile.wageFormat]}</Field>
          )}
        </div>
      )}
    </>
  );

  const foot = summary ? (
    <div className={classes.summary}>
      <Total label="Total time:" hours={formatNumber(summary.totalTime)}>
        <span>
          {typeof summary.weekHours !== 'undefined' && (
            <Field label="Hours this week:">
              <Hours value={formatNumber(summary.weekHours)} />
            </Field>
          )}
        </span>
        <Field label="Allocated OT:">
          <Hours value={formatNumber(summary.overtime)} />
        </Field>
      </Total>
    </div>
  ) : (
    <div className={classes.item}>No work is done</div>
  );

  return (
    <div className={classes.day}>
      {blocks.map(({ key, title, last, node }, i) => (
        // bar + first item and last item + footer share a block so they never split
        <div key={key} className={classes.block}>
          {!i && head}
          {title && <div className={cs(classes.title, !node && classes.last)}>{title}</div>}
          {node && !title && <div className={classes.dots} />}
          {node && <div className={cs(classes.item, last && classes.last)}>{node}</div>}
          {i === blocks.length - 1 && foot}
        </div>
      ))}
    </div>
  );
};

export default DayPrint;
